/**
 * Auctioneer overlay — private, dark. Shows the current player, current bid,
 * next-min bid, highest bidder and running bid history. Timer flashes red in
 * the last 5 seconds. SaleBanner honors custom SOLD / UNSOLD media.
 */
import React from "react";
import { useParams } from "react-router-dom";
import { Trophy } from "@phosphor-icons/react";
import { resolveImageUrl } from "@/components/ImageInput";
import {
  PHOTO_FALLBACK, SaleBanner, useSaleAnnouncement, useCountdown, usePublicAuction, useOverlayState,
  FIELD_LABELS, readPlayerField, getLedDisplayFields, useAccentStyle, brandLogoFor,
} from "./_shared";

export function OverlayAuctioneer() {
  const { auctionId } = useParams();
  const { auction, teams, players, reload } = usePublicAuction(auctionId);
  const announce = useSaleAnnouncement(auctionId, players, teams);
  const state = useOverlayState(auctionId, reload);

  const timer = useCountdown(state?.timer_ends_at, state?.status);
  const cur = state?.current_player;
  const bidder = state?.current_bidder_team;
  const increment = state?.next_step || state?.settings?.bid_increment || 10;
  const nextBid = bidder ? (state?.current_bid || 0) + increment : (state?.current_bid || 0);
  const ledExtraFields = getLedDisplayFields(auction);
  const accentStyle = useAccentStyle(auction);

  return (
    <div className="min-h-screen text-white p-6 led-bg relative" data-testid="overlay-auctioneer" style={{ fontFamily: "Inter, sans-serif", ...accentStyle }}>
      <SaleBanner announce={announce} auction={auction} position="center" />
      <div className="absolute inset-y-0 left-0 w-20 z-0 pointer-events-none" style={{ background: "linear-gradient(135deg, var(--accent-mid) 0%, transparent 100%)", clipPath: "polygon(0 0, 100% 0, 60% 100%, 0 100%)" }} />
      <div className="absolute inset-y-0 right-0 w-32 z-0 pointer-events-none" style={{ background: "linear-gradient(225deg, var(--accent-mid) 0%, transparent 100%)", clipPath: "polygon(40% 0, 100% 0, 100% 100%, 0 100%)" }} />

      <div className="relative z-10 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <img src={brandLogoFor(auction)} alt="" className="h-11 drop-shadow-2xl" />
          <div className="text-center flex-1">
            <div className="text-yellow-400 font-black uppercase text-lg tracking-widest">AUCTIONEER</div>
            <div className="flex justify-center items-stretch mt-1">
              <span className="text-black font-black uppercase text-lg px-3 py-0.5 tracking-wider" style={{ background: "var(--accent-color)" }}>PLAYER</span>
              <span className="bg-white text-black font-black uppercase text-lg px-3 py-0.5 tracking-wider">CONTROL</span>
            </div>
          </div>
          <div className={`font-black text-3xl ${timer <= 5 && timer > 0 ? "text-red-400" : "text-yellow-400"}`}>{timer > 0 ? `${timer}s` : "—"}</div>
        </div>

        {!cur ? (
          <div className="border-2 border-dashed border-orange-500/40 rounded-lg p-16 text-center bg-black/40 backdrop-blur">
            <div className="font-black text-6xl uppercase text-yellow-400">No Active Player</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-lg p-4 bg-black/50 backdrop-blur border-2" style={{ borderColor: "var(--accent-color)" }}>
              <div className="w-full aspect-[3/4] rounded mb-4 border border-orange-500/40 bg-black/50 flex items-center justify-center overflow-hidden">
                <img
                  src={cur.photo_url ? resolveImageUrl(cur.photo_url) : PHOTO_FALLBACK}
                  onError={(e) => { e.target.src = PHOTO_FALLBACK; }}
                  alt=""
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="label-mono text-orange-300">{cur.role} · Cat {cur.category}{cur.player_number ? ` · #${cur.player_number}` : ""}</div>
              <div className="font-black text-4xl uppercase leading-tight text-white">{cur.name}</div>
              <div className="text-zinc-300 mt-1">{cur.location} · Base {cur.base_price}</div>
              {cur.batting_style && <div className="text-zinc-400 text-sm mt-1"><span className="text-orange-300 font-bold">BAT</span> {cur.batting_style}</div>}
              {cur.bowling_style && <div className="text-zinc-400 text-sm"><span className="text-orange-300 font-bold">BOWL</span> {cur.bowling_style}</div>}
              {cur.mobile && <div className="text-zinc-400 text-sm"><span className="text-orange-300 font-bold">MOB</span> {cur.mobile}</div>}
              {/* Iter24: extra fields chosen by superadmin for LED display */}
              {ledExtraFields.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm border-t border-orange-500/20 pt-2" data-testid="auct-extra-stats">
                  {ledExtraFields.map((k) => {
                    const v = readPlayerField(cur, k);
                    if (v == null || v === "") return null;
                    return (
                      <div key={k} className="text-zinc-300 truncate" data-testid={`auct-extra-${k}`}>
                        <span className="text-orange-300 font-bold mr-1">{FIELD_LABELS[k] || k.toUpperCase()}</span>{v}
                      </div>
                    );
                  })}
                </div>
              )}
              {cur.notes && <div className="text-zinc-300 mt-2 text-sm border-t border-orange-500/20 pt-2">{cur.notes}</div>}
            </div>

            <div className="space-y-4">
              <div className="rounded-lg p-6 bg-black/50 backdrop-blur border-2" style={{ borderColor: "var(--accent-color)" }}>
                <div className="label-mono text-orange-300">CURRENT BID</div>
                <div className="font-black text-8xl leading-none text-yellow-400 drop-shadow-2xl">{state.current_bid}</div>
                <div className="label-mono text-white/80 mt-1">Next min · {nextBid} pts</div>
              </div>
              <div className="rounded-lg p-6 bg-black/50 backdrop-blur border-2" style={{ borderColor: "var(--accent-color)" }}>
                <div className="label-mono text-orange-300 mb-2">HIGHEST BIDDER</div>
                {bidder ? (
                  <div className="flex items-center gap-3 mt-2">
                    {bidder.logo_url ? (
                      <img src={resolveImageUrl(bidder.logo_url)} alt="" className="w-14 h-14 rounded object-cover border-2" style={{ borderColor: bidder.color || "#F97316" }} />
                    ) : (
                      <div className="w-14 h-14 grid place-items-center rounded border-2" style={{ background: `${bidder.color}22`, borderColor: bidder.color || "#F97316" }}>
                        <Trophy weight="fill" size={24} style={{ color: bidder.color || "#F97316" }} />
                      </div>
                    )}
                    <div>
                      <div className="font-black text-2xl leading-none uppercase text-white">{bidder.name}</div>
                      <div className="label-mono text-orange-300">{bidder.short_name}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-zinc-500 mt-2">No bids yet</div>
                )}
              </div>

              <div className="rounded-lg p-6 bg-black/50 backdrop-blur border-2" style={{ borderColor: "var(--accent-color)" }}>
                <div className="label-mono text-orange-300 mb-2">BID HISTORY</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {(state?.bid_history || []).slice().reverse().map((b, i) => (
                    <div key={i} className="text-sm flex justify-between border-b border-orange-500/20 py-1">
                      <span className="text-white">{b.team_short || b.team_name || "Floor"}</span>
                      <span className="text-yellow-400 font-mono font-bold">{b.amount} pts</span>
                    </div>
                  ))}
                  {(!state?.bid_history || state.bid_history.length === 0) && <div className="text-zinc-500 text-sm">No bids yet.</div>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
